import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import {
  Copy, Download, Sparkles, Twitter, Facebook, Share2, Rocket,
  Globe, Award, TrendingUp, MessageCircle, Wallet,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AFF } from "@/constants/testIds";
import BrandMark from "@/components/lifted/BrandMark";
import AffiliateBillingBadge from "@/components/affiliate/AffiliateBillingBadge";
import EcosystemRoleDiagram from "@/components/dashboard/EcosystemRoleDiagram";
import LiftedSuppliesOverview from "@/components/lifted_supplies/LiftedSuppliesOverview";

const TIER_COLOR = { "Lift-Off": "#8ee400", Orbit: "#b6ff1a", Galaxy: "#8b3fc2", Cosmos: "#f3f5f0" };

function dollars(cents) { return `$${((cents || 0) / 100).toFixed(2)}`; }

export default function AffiliateDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [tagline, setTagline] = useState("");
  const [bio, setBio] = useState("");
  const [copied, setCopied] = useState("");

  const reload = async () => {
    try {
      const { data } = await api.get("/affiliate/me");
      setData(data);
      setTagline(data.profile.tagline || "");
      setBio(data.profile.bio || "");
    } catch (e) { setErr(apiErrorMessage(e)); }
  };
  useEffect(() => { reload(); }, []);

  const copy = async (kind, value) => {
    try { await navigator.clipboard.writeText(value); setCopied(kind); setTimeout(() => setCopied(""), 1500); }
    catch (_e) { /* noop */ }
  };
  const downloadQr = () => {
    const blob = new Blob([data.share.qr_svg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `lifted-${data.profile.slug}.svg`; a.click(); URL.revokeObjectURL(url);
  };
  const saveProfile = async () => {
    setSavingProfile(true);
    try { await api.put("/affiliate/me/profile", { tagline, bio }); await reload(); }
    catch (e) { setErr(apiErrorMessage(e)); }
    finally { setSavingProfile(false); }
  };

  if (err) return <div className="text-rose-300">{err}</div>;
  if (!data) return <div className="font-bebas tracking-[0.3em] text-lifted-dim">LOADING AFFILIATE HUB…</div>;

  const p = data.profile;
  const c = data.training_credibility;
  const tier = data.tier;
  const tColor = TIER_COLOR[tier] || "#b6ff1a";
  const next = data.next_tier;
  const ledger = data.ledger || [];
  const cfg = data.commission_config;
  const configured = Object.entries(cfg).some(([k, v]) => k.endsWith("_pct") && v != null);

  const shareUrl = encodeURIComponent(p.referral_link);
  const shareText = encodeURIComponent(`Join me on Lifted → ${p.referral_link}`);

  return (
    <div data-testid={AFF.dashboard} className="space-y-10">
      <header className="fade-up">
        <BrandMark tag="logo.affiliate.primary" size="lg" className="mb-4" testId="affiliate-zone-logo" />
        <div className="eyebrow flex items-center gap-2"><Rocket className="h-3 w-3" /> Affiliate command</div>
        <h1 className="headline-xl">
          {user?.name?.split(" ")[0]}'s <span style={{ color: tColor }}>{tier}</span> deck
        </h1>
        <p className="text-lifted-dim mt-3 max-w-2xl">
          Your unified Lifted affiliate hub. Share your code, watch referrals land, climb the tier ladder.
        </p>
      </header>

      {/* Phase 14 — Billing status badge */}
      <AffiliateBillingBadge />

      {/* Phase 16 — Interactive ecosystem role diagram */}
      <EcosystemRoleDiagram me={user} />

      {/* Share row */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="glass-panel p-6 lg:col-span-2 fade-up delay-1">
          <div className="eyebrow !text-lifted-text-dim flex items-center gap-2"><Globe className="h-3 w-3" /> Your referral hub</div>
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="font-anton text-3xl uppercase break-all">
              <span data-testid={AFF.referralCode} style={{ color: tColor }}>{p.referral_code}</span>
            </div>
            <button data-testid={AFF.copyCodeBtn} onClick={() => copy("code", p.referral_code)} className="btn-ghost !text-[0.7rem]">
              <Copy className="h-3 w-3" /> {copied === "code" ? "Copied" : "Copy code"}
            </button>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <code data-testid={AFF.referralLink} className="text-sm text-lifted-dim break-all">{p.referral_link}</code>
            <div className="flex flex-wrap gap-2">
              <button data-testid={AFF.copyLinkBtn} onClick={() => copy("link", p.referral_link)} className="btn-lifted !py-2 !px-3 !text-[0.7rem]">
                <Copy className="h-3 w-3" /> {copied === "link" ? "Copied" : "Copy link"}
              </button>
              <a data-testid={AFF.shareTwitter} target="_blank" rel="noreferrer" href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}`} className="btn-ghost !py-2 !px-3 !text-[0.7rem]"><Twitter className="h-3 w-3" /> X</a>
              <a data-testid={AFF.shareFacebook} target="_blank" rel="noreferrer" href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} className="btn-ghost !py-2 !px-3 !text-[0.7rem]"><Facebook className="h-3 w-3" /> Share</a>
              <a data-testid={AFF.shareWhatsapp} target="_blank" rel="noreferrer" href={`https://wa.me/?text=${shareText}`} className="btn-ghost !py-2 !px-3 !text-[0.7rem]"><Share2 className="h-3 w-3" /> WhatsApp</a>
            </div>
          </div>
          <div className="mt-5 text-xs text-lifted-mute font-bebas tracking-[0.22em]">
            Public hub · <Link to={`/affiliate/u/${p.slug}`} target="_blank" rel="noreferrer" className="text-lifted-green underline">/affiliate/u/{p.slug}</Link>
          </div>
          {/* Phase 3A.5.c — Custom slug editor */}
          <SlugEditor currentSlug={p.slug} onSaved={(newProfile) => {
            // Optimistic local update — parent will refetch on next nav.
            p.slug = newProfile.slug;
            p.referral_link = newProfile.referral_link || p.referral_link;
          }} />
          {/* Phase 3A.5.b — Certificate link */}
          <CertificateButton />
        </div>

        <div className="glass-panel p-6 fade-up delay-2 flex flex-col items-center">
          <div className="eyebrow !text-lifted-text-dim self-start">QR code</div>
          <div data-testid={AFF.qrCode} className="mt-3 p-3 bg-lifted-black rounded-xl border border-lifted-green/20"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(p.qr_svg || "", { USE_PROFILES: { svg: true, svgFilters: true } }) }} />
          <button data-testid={AFF.downloadQr} onClick={downloadQr} className="btn-ghost mt-4 !text-[0.7rem]">
            <Download className="h-3 w-3" /> Download SVG
          </button>
        </div>
      </section>

      {/* Tier ladder */}
      <section className="glass-panel p-6 fade-up">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="eyebrow !text-lifted-text-dim flex items-center gap-2"><TrendingUp className="h-3 w-3" /> Tier ladder</div>
            <div className="mt-2 flex items-center gap-3">
              <span data-testid={AFF.tierBadge} className="font-anton text-3xl uppercase" style={{ color: tColor }}>{tier}</span>
              {next && <span data-testid={AFF.tierNext} className="text-xs text-lifted-dim">
                → Next: <span className="text-lifted-text">{next.label}</span>
                {next.referrals_needed > 0 && <> · {next.referrals_needed} referrals to go</>}
              </span>}
            </div>
          </div>
          <div className="flex gap-2">
            {["Lift-Off", "Orbit", "Galaxy", "Cosmos"].map((k) => (
              <span key={k} className="chip" style={{ background: tier === k ? `${TIER_COLOR[k]}25` : undefined, color: tier === k ? TIER_COLOR[k] : undefined, borderColor: tier === k ? TIER_COLOR[k] : undefined }}>
                {k}
              </span>
            ))}
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <Stat label="Referrals" value={p.stats.referrals_count} />
          <Stat label="Conversions" value={p.stats.conversions_count} />
          <Stat label="Gross volume" value={dollars(p.stats.gross_volume_cents)} />
          <Stat label="Lifetime commission" value={dollars(p.stats.lifetime_commission_cents)} />
        </div>
      </section>

      {/* Commission ledger */}
      <section className="glass-panel p-6 fade-up">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="eyebrow !text-lifted-text-dim flex items-center gap-2"><Wallet className="h-3 w-3" /> Commission ledger</div>
          {!configured && (
            <span className="chip chip--purple !text-[0.6rem]">No commission % configured · entries land as pending_review · $0</span>
          )}
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          {[
            ["Pending", data.ledger_amounts.pending_review],
            ["Approved", data.ledger_amounts.approved],
            ["Paid", data.ledger_amounts.paid],
            ["Void", data.ledger_amounts.void],
          ].map(([k, v]) => (
            <Stat key={k} label={k} value={dollars(v)} />
          ))}
        </div>
        <table className="w-full mt-5 text-sm">
          <thead>
            <tr className="text-left text-[0.7rem] tracking-[0.22em] uppercase text-lifted-mute font-bebas border-b border-white/5">
              <th className="py-2">When</th><th>Source</th><th>Order</th><th>Amount</th><th>Pct</th><th>Commission</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {ledger.slice(0, 20).map((e, idx) => (
              <tr key={`${e.id || "row"}-${idx}`} className="border-b border-white/5">
                <td className="py-2 text-xs text-lifted-dim whitespace-nowrap">{e.created_at?.slice(0, 16).replace("T", " ")}</td>
                <td><span className="chip">{e.order_source}</span></td>
                <td className="text-xs text-lifted-dim break-all">{e.order_id}</td>
                <td className="text-lifted-text">{dollars(e.order_amount_cents)}</td>
                <td className="text-lifted-text">{e.commission_pct == null ? "—" : `${e.commission_pct}%`}</td>
                <td className="text-lifted-green">{dollars(e.commission_cents)}</td>
                <td><LedgerChip status={e.status} /></td>
              </tr>
            ))}
            {ledger.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-lifted-mute">No commission events yet. Share your link.</td></tr>}
          </tbody>
        </table>
      </section>

      {/* Training credibility */}
      <section className="glass-panel p-6 fade-up">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="eyebrow !text-lifted-text-dim flex items-center gap-2"><Award className="h-3 w-3" /> Training credibility</div>
          <Link to="/training/affiliate" className="btn-ghost !text-[0.7rem]"><Sparkles className="h-3 w-3" /> Go to Affiliate track</Link>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Stat label="Modules complete" value={`${c.modules_completed} / ${c.modules_total}`} />
          <Stat label="Affiliate certified" value={c.affiliate_certified ? "YES" : "NOT YET"} highlight={c.affiliate_certified} />
          <Stat label="Trust badges earned" value={c.badges.length} />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {c.badges.length === 0
            ? <span className="text-xs text-lifted-mute">Complete training to unlock public trust badges on your hub.</span>
            : c.badges.map((b) => (
              <span key={b.key} className="chip" style={{ background: `${b.color}25`, color: b.color, borderColor: b.color }}>{b.label}</span>
            ))}
        </div>
      </section>

      {/* Profile editor */}
      <section className="glass-panel p-6 fade-up">
        <div className="eyebrow !text-lifted-text-dim flex items-center gap-2"><MessageCircle className="h-3 w-3" /> Public hub copy</div>
        <p className="text-xs text-lifted-dim mt-1">Shown on your public referral page at <code className="text-lifted-green">/affiliate/u/{p.slug}</code>.</p>
        <div className="mt-3 space-y-3">
          <div>
            <label className="lifted-label">Tagline</label>
            <input data-testid={AFF.taglineInput} className="input-lifted" value={tagline} onChange={(e) => setTagline(e.target.value)} maxLength={160} />
          </div>
          <div>
            <label className="lifted-label">Bio</label>
            <textarea data-testid={AFF.bioInput} className="input-lifted min-h-[110px]" value={bio} onChange={(e) => setBio(e.target.value)} maxLength={600} />
          </div>
          <button data-testid={AFF.saveProfile} onClick={saveProfile} disabled={savingProfile} className="btn-lifted !text-[0.7rem]">
            {savingProfile ? "Saving…" : "Save hub copy"}
          </button>
        </div>
      </section>

      {/* Phase 19 — Lifted Supplies network surface for affiliates */}
      <LiftedSuppliesOverview />
    </div>
  );
}

function Stat({ label, value, highlight = false }) {
  return (
    <div className="glass-panel p-4">
      <div className="eyebrow !text-lifted-text-dim">{label}</div>
      <div className={`mt-1 font-anton text-2xl ${highlight ? "text-lifted-green" : "text-lifted-text"}`}>{value}</div>
    </div>
  );
}

function LedgerChip({ status }) {
  const map = {
    pending_review: { label: "Pending", cls: "chip" },
    approved:       { label: "Approved", cls: "chip chip--green" },
    paid:           { label: "Paid", cls: "chip chip--purple" },
    void:           { label: "Void", cls: "chip" },
  };
  const m = map[status] || { label: status, cls: "chip" };
  return <span className={m.cls}>{m.label}</span>;
}

// Phase 3A.5.c — affiliate self-service slug editor with reserved-word +
// uniqueness validation handled server-side.
function SlugEditor({ currentSlug, onSaved }) {
  const [editing, setEditing] = React.useState(false);
  const [value, setValue] = React.useState(currentSlug || "");
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState(null);

  async function save() {
    setSaving(true); setMsg(null);
    try {
      const r = await fetch(`/api/affiliate/me/slug`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug: value }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || "Could not save slug");
      setMsg({ type: "ok", text: "Custom link saved." });
      setEditing(false);
      if (onSaved) onSaved(body.profile);
    } catch (e) {
      setMsg({ type: "err", text: e.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-4" data-testid="slug-editor">
      {!editing ? (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-[10px] uppercase tracking-[0.18em] text-lifted-green underline"
          data-testid="slug-editor-toggle"
        >
          Edit custom link
        </button>
      ) : (
        <div className="flex flex-col sm:flex-row gap-2 items-stretch">
          <div className="flex items-center bg-black/40 border border-white/15 rounded-md overflow-hidden">
            <span className="px-2 text-xs text-lifted-dim">/</span>
            <input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="your-slug"
              className="px-2 py-2 bg-transparent text-white text-sm focus:outline-none"
              data-testid="slug-editor-input"
            />
          </div>
          <button
            type="button"
            onClick={save}
            disabled={saving || !value.trim()}
            className="btn-lifted !py-2 !px-3 !text-[0.7rem] disabled:opacity-50"
            data-testid="slug-editor-save"
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            onClick={() => { setEditing(false); setValue(currentSlug || ""); setMsg(null); }}
            className="btn-ghost !text-[0.7rem]"
          >
            Cancel
          </button>
        </div>
      )}
      {msg && (
        <div
          className={`text-[10px] mt-2 ${msg.type === "ok" ? "text-lifted-green" : "text-rose-300"}`}
          data-testid="slug-editor-msg"
        >
          {msg.text}
        </div>
      )}
    </div>
  );
}

// Phase 3A.5.b — Certificate view/download button. Hits the affiliate-me
// certificate endpoint; opens the printable HTML in a new tab.
function CertificateButton() {
  const [state, setState] = React.useState({ loading: false, error: null });

  async function open() {
    setState({ loading: true, error: null });
    try {
      const r = await fetch(`/api/affiliate/me/certificate`, { credentials: "include" });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || "Certificate not available yet");
      // Open downloadable HTML version in a new tab
      window.open(`/api/affiliate/me/certificate/download`, "_blank", "noopener");
      setState({ loading: false, error: null });
    } catch (e) {
      setState({ loading: false, error: e.message });
    }
  }

  return (
    <div className="mt-3" data-testid="certificate-button-row">
      <button
        type="button"
        onClick={open}
        disabled={state.loading}
        className="btn-ghost !text-[0.7rem] disabled:opacity-50"
        data-testid="certificate-view-btn"
      >
        {state.loading ? "Loading…" : "View / Download certificate"}
      </button>
      {state.error && (
        <div className="text-[10px] text-rose-300 mt-1" data-testid="certificate-error">
          {state.error}
        </div>
      )}
    </div>
  );
}
